package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.FilterChip
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.Quote
import com.example.positionahead.model.SymbolPerformance
import com.example.positionahead.model.SymbolPriceHistory
import com.example.positionahead.ui.components.StockLineChart
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatPercent

data class ChartRange(val label: String, val days: Int)

val CHART_RANGES = listOf(
    ChartRange("1M", 30),
    ChartRange("3M", 90),
    ChartRange("6M", 180),
    ChartRange("1Y", 365),
)

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun MarketChartScreen(
    chartSymbol: String,
    chartRangeDays: Int,
    chartHistory: SymbolPriceHistory?,
    chartQuote: Quote?,
    chartPerformance: SymbolPerformance?,
    symbolSuggestions: List<String>,
    onSymbolChange: (String) -> Unit,
    onRangeChange: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    var symbolInput by remember(chartSymbol) { mutableStateOf(chartSymbol) }

    val quote = chartQuote
    val changeColor = when {
        quote == null -> MaterialTheme.colorScheme.onSurface
        quote.changePercent >= 0 -> gainColor
        else -> lossColor
    }
    val historyPoints = chartHistory?.points.orEmpty()
    val rangeChange = if (historyPoints.size >= 2) {
        val first = historyPoints.first().close
        val last = historyPoints.last().close
        if (first > 0) (last - first) / first * 100.0 else null
    } else null

    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        item {
            Text(
                "Market charts — research any symbol before you buy.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
            )
        }

        item {
            OutlinedTextField(
                value = symbolInput,
                onValueChange = {
                    symbolInput = it.uppercase()
                    if (symbolInput.length in 1..6) onSymbolChange(symbolInput)
                },
                label = { Text("Symbol") },
                placeholder = { Text("QQQM") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
            )
        }

        item {
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                symbolSuggestions.take(12).forEach { symbol ->
                    FilterChip(
                        selected = symbol.equals(chartSymbol, ignoreCase = true),
                        onClick = {
                            symbolInput = symbol
                            onSymbolChange(symbol)
                        },
                        label = { Text(symbol) },
                    )
                }
            }
        }

        item {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
            ) {
                Column(
                    modifier = Modifier.padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Text(
                        text = chartSymbol,
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold,
                    )
                    quote?.name?.takeIf { it.isNotBlank() && it != chartSymbol }?.let { name ->
                        Text(name, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f))
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.Bottom,
                    ) {
                        Text(
                            text = quote?.let { formatCurrency(it.price) } ?: "—",
                            style = MaterialTheme.typography.displaySmall,
                            fontWeight = FontWeight.Bold,
                        )
                        Column(horizontalAlignment = Alignment.End) {
                            quote?.let {
                                Text(
                                    formatPercent(it.changePercent),
                                    style = MaterialTheme.typography.titleMedium,
                                    color = changeColor,
                                    fontWeight = FontWeight.SemiBold,
                                )
                            }
                            rangeChange?.let { change ->
                                Text(
                                    "${CHART_RANGES.find { it.days == chartRangeDays }?.label ?: ""} ${formatPercent(change)}",
                                    style = MaterialTheme.typography.labelMedium,
                                    color = if (change >= 0) gainColor else lossColor,
                                )
                            }
                        }
                    }

                    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        CHART_RANGES.forEach { range ->
                            FilterChip(
                                selected = chartRangeDays == range.days,
                                onClick = { onRangeChange(range.days) },
                                label = { Text(range.label) },
                            )
                        }
                    }

                    if (historyPoints.size >= 2) {
                        StockLineChart(points = historyPoints, heightDp = 220)
                    } else {
                        Text(
                            "Loading chart data…",
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(vertical = 48.dp),
                        )
                    }
                }
            }
        }

        item {
            Card(modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Key statistics", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.SemiBold)
                    HorizontalDivider()
                    StatRow("Previous close", quote?.let { formatCurrency(it.price * (1 - it.changePercent / 100)) } ?: "—")
                    StatRow(
                        "52 week range",
                        if (quote?.yearLow != null && quote.yearHigh != null) {
                            "${formatCurrency(quote.yearLow)} – ${formatCurrency(quote.yearHigh)}"
                        } else "—",
                    )
                    StatRow("Dividend yield", quote?.takeIf { it.dividendYield > 0 }?.let { "${it.dividendYield}%" } ?: "—")
                    StatRow("P/E ratio", quote?.peRatio?.takeIf { it > 0 }?.let { "${(it * 10).toInt() / 10.0}" } ?: "—")
                    StatRow("1 month return", chartPerformance?.return1MonthPercent?.let { formatPercent(it) } ?: "—")
                    StatRow("YTD return", chartPerformance?.returnYtdPercent?.let { formatPercent(it) } ?: "—")
                }
            }
        }
    }
}

@Composable
private fun StatRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(label, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f))
        Text(value, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Medium)
    }
}
